/**
 * Stage driver. Reads the stage table and advances the machine.
 *
 * One loop for every state: play the animation if its asset exists, honour the dwell,
 * then move to the declared next state. States that await a visitor action or an async
 * result simply stop here and are advanced by the caller.
 */
import { STAGES } from '../core/stages';
import { assetUrl, assetExists } from '../core/assets';
import type { Machine, V1State } from '../core/machine';

const reduced = (): boolean =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Plays a stage clip. Resolves immediately when the asset is absent or motion is reduced. */
async function playAnimation(root: HTMLElement, alias: string, url: string): Promise<void> {
  await new Promise<void>((resolve) => {
    const v = document.createElement('video');
    v.className = 'cc-stage-video';
    v.dataset['stage'] = alias;
    v.src = url;
    v.muted = true;
    v.playsInline = true;
    v.preload = 'auto';
    v.setAttribute('aria-hidden', 'true');
    root.append(v);

    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      v.remove();            // removed, never faded — a fade is itself a visible change
      resolve();
    };
    v.addEventListener('ended', finish, { once: true });
    // Never strand a visitor on a stalled clip.
    window.setTimeout(finish, 15_000);
    v.play().catch(finish);  // autoplay refusal is normal; the journey does not depend on it
  });
}

const wait = (ms: number): Promise<void> =>
  new Promise((r) => window.setTimeout(r, ms));

/**
 * Runs one stage to completion and returns the next state, or null when the stage
 * awaits something external.
 */
export async function runStage(root: HTMLElement, machine: Machine): Promise<V1State | null> {
  const state = machine.ctx.state;
  const stage = STAGES[state];

  if (stage.animation && !reduced()) {
    const alias = stage.animation;
    if (await assetExists(alias)) {
      await playAnimation(root, alias, assetUrl(alias));
    }
  }
  if (stage.dwellMs) await wait(stage.dwellMs);

  if (stage.awaits) return null;
  return stage.next ?? null;
}

/** Advances automatically until a stage awaits the visitor or an async result. */
export async function advance(root: HTMLElement, machine: Machine): Promise<void> {
  for (;;) {
    const next = await runStage(root, machine);
    if (!next || machine.ctx.state === 'FAILED') return;
    machine.go(next);
  }
}
